import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMissatgeError } from '@/api/errors';
import {
  useAppVersions,
  useDesa,
  useElimina,
  useEtag,
  useMissatgesAdmin,
  type AppVersioDto,
  type MissatgeAdminDto,
  type Plataforma,
} from '@/api/administracio';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp, Modal } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Confirmacio } from './marc/Calaix';
import { CapPanell, ErrorDesa } from './comu';
import {
  comparaVersions,
  isoALocal,
  localAIso,
  versioMaxima,
  versioValida,
  RE_VERSIO,
} from './sistema/utils';
import './administracio.css';
import './sistema.css';

const PLATAFORMES: Plataforma[] = ['IOS', 'ANDROID'];
const TIPUS_MISSATGE = ['INFO', 'ACTUALITZACIO', 'VERSIO_MINIMA', 'MANTENIMENT'];
type Pestanya = 'versions' | 'missatges';
const IDIOMES_PREVIA = ['ca', 'es'] as const;

function PreviaMobil({ titol, cos }: { titol: string; cos: string }) {
  const { t } = useTranslation('administracio');
  return (
    <div className="mobil-previa" aria-label={t('versions.previa')}>
      <div className="mobil-previa__popup">
        <Icona nom="exclamationmark-triangle" />
        <strong>{titol || t('versions.titolCa')}</strong>
        <p>{cos}</p>
        <span className="btn btn--dark btn--s">{t('versions.dacord')}</span>
      </div>
    </div>
  );
}

function NovaVersio({ onTanca }: { onTanca: () => void }) {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const desa = useDesa<AppVersioDto>('/app/versions', ['app', 'versions']);
  const [v, setV] = useState<AppVersioDto>({
    plataforma: 'IOS',
    versio: '',
    minimaObligatoria: false,
  });
  const versioOk = RE_VERSIO.test(v.versio.trim());
  return (
    <Modal
      titol={t('versions.nova')}
      icona={<Icona nom="arrow-up" className="c-blue icon-20" />}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!versioOk || desa.isPending}
            onClick={() => desa.mutate({ ...v, versio: v.versio.trim() }, { onSuccess: onTanca })}
          >
            {t('versions.publica')}
          </button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 12 }}>
        <Camp etiqueta={t('versions.plataforma')}>
          <Desplegable
            aria-label={t('versions.plataforma')}
            valor={v.plataforma}
            opcions={PLATAFORMES.map((p) => ({
              valor: p,
              etiqueta: t(`versions.plataformes.${p}`),
            }))}
            onCanvia={(plataforma) => setV({ ...v, plataforma })}
          />
        </Camp>
        <Camp etiqueta={t('versions.versio')}>
          <input
            className="input mono"
            value={v.versio}
            placeholder="2.4.1"
            aria-invalid={v.versio !== '' && !versioOk}
            onChange={(e) => setV({ ...v, versio: e.target.value })}
          />
        </Camp>
      </div>
      {v.versio !== '' && !versioOk ? (
        <p className="error-text">{t('sistema.versions.formatVersio')}</p>
      ) : null}
      <Camp etiqueta={t('versions.build')}>
        <input
          className="input"
          value={v.build ?? ''}
          onChange={(e) => setV({ ...v, build: e.target.value || null })}
        />
      </Camp>
      <div className="grid grid-2" style={{ gap: 12 }}>
        <Camp etiqueta={t('versions.notesCa')}>
          <textarea
            className="input"
            value={v.notesCa ?? ''}
            onChange={(e) => setV({ ...v, notesCa: e.target.value || null })}
          />
        </Camp>
        <Camp etiqueta={t('versions.notesEs')}>
          <textarea
            className="input"
            value={v.notesEs ?? ''}
            onChange={(e) => setV({ ...v, notesEs: e.target.value || null })}
          />
        </Camp>
      </div>
      <Camp etiqueta={t('versions.urlBotiga')}>
        <input
          className="input"
          type="url"
          value={v.urlBotiga ?? ''}
          onChange={(e) => setV({ ...v, urlBotiga: e.target.value || null })}
        />
      </Camp>
      <label className="switch-row">
        <span>
          {t('versions.minimaObligatoria')}
          <span className="camp__ajuda" style={{ display: 'block' }}>
            {t('sistema.versions.ajudaMinima')}
          </span>
        </span>
        <input
          type="checkbox"
          className="switch switch--critic"
          checked={v.minimaObligatoria}
          onChange={(e) => setV({ ...v, minimaObligatoria: e.target.checked })}
        />
      </label>
      <p className="camp__ajuda" style={{ margin: 0 }}>
        <Icona nom="info-circle" /> {t('sistema.versions.definitiva')}
      </p>
      {desa.isError ? <p className="error-text">{missatgeError(desa.error)}</p> : null}
    </Modal>
  );
}

function EditaMissatge({ inicial, onTanca }: { inicial: MissatgeAdminDto; onTanca: () => void }) {
  const { t, i18n } = useTranslation('administracio');
  const { etag } = useEtag('/app/missatges', inicial.id);
  const desa = useDesa<MissatgeAdminDto>('/app/missatges', ['app', 'missatges'], etag);
  const [m, setM] = useState(inicial);
  const [idiomaPrevia, setIdiomaPrevia] = useState<'ca' | 'es'>(
    i18n.language === 'es' ? 'es' : 'ca',
  );
  const commuta = (p: Plataforma) =>
    setM({
      ...m,
      plataformes: m.plataformes.includes(p)
        ? m.plataformes.filter((x) => x !== p)
        : [...m.plataformes, p],
    });
  const versionsOk = versioValida(m.versioMin) && versioValida(m.versioMax);
  const rangOk =
    !m.versioMin || !m.versioMax || !versionsOk || comparaVersions(m.versioMin, m.versioMax) <= 0;
  const datesOk = !m.desde || !m.fins || new Date(m.desde) < new Date(m.fins);
  const valid =
    !!m.titolCa.trim() &&
    !!m.titolEs.trim() &&
    m.plataformes.length > 0 &&
    versionsOk &&
    rangOk &&
    datesOk;
  return (
    <Modal
      titol={inicial.id == null ? t('versions.nouMissatge') : t('versions.editaMissatge')}
      onTanca={onTanca}
      icona={<Icona nom="megaphone" className="c-blue icon-20" />}
      ample
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!valid || desa.isPending}
            onClick={() => desa.mutate(m, { onSuccess: onTanca })}
          >
            <Icona nom="checkmark" />
            {t('comu.desa')}
          </button>
        </>
      }
    >
      <div className="admin-mes-lateral">
        <div className="form">
          <div className="grid grid-2" style={{ gap: 12 }}>
            <Camp etiqueta={t('versions.tipus')}>
              <Desplegable
                aria-label={t('versions.tipus')}
                valor={m.tipus}
                opcions={TIPUS_MISSATGE.map((tp) => ({
                  valor: tp,
                  etiqueta: t(`versions.tipusMissatge.${tp}`),
                }))}
                onCanvia={(tipus) => setM({ ...m, tipus })}
              />
            </Camp>
            <div className="camp">
              <span className="camp__label">{t('sistema.versions.plataformes')}</span>
              <div className="chips">
                {PLATAFORMES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="chip"
                    aria-pressed={m.plataformes.includes(p)}
                    onClick={() => commuta(p)}
                  >
                    <Icona nom={p === 'IOS' ? 'apple' : 'android'} />
                    {t(`versions.plataformes.${p}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Camp etiqueta={t('versions.titolCa')}>
            <input
              className="input"
              value={m.titolCa}
              onFocus={() => setIdiomaPrevia('ca')}
              onChange={(e) => setM({ ...m, titolCa: e.target.value })}
            />
          </Camp>
          <Camp etiqueta={t('versions.cosCa')}>
            <textarea
              className="input"
              value={m.cosCa}
              onFocus={() => setIdiomaPrevia('ca')}
              onChange={(e) => setM({ ...m, cosCa: e.target.value })}
            />
          </Camp>
          <Camp etiqueta={t('versions.titolEs')}>
            <input
              className="input"
              value={m.titolEs}
              onFocus={() => setIdiomaPrevia('es')}
              onChange={(e) => setM({ ...m, titolEs: e.target.value })}
            />
          </Camp>
          <Camp etiqueta={t('versions.cosEs')}>
            <textarea
              className="input"
              value={m.cosEs}
              onFocus={() => setIdiomaPrevia('es')}
              onChange={(e) => setM({ ...m, cosEs: e.target.value })}
            />
          </Camp>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <Camp etiqueta={t('sistema.versions.desde')}>
              <input
                className="input"
                type="datetime-local"
                value={isoALocal(m.desde)}
                onChange={(e) => setM({ ...m, desde: localAIso(e.target.value) })}
              />
            </Camp>
            <Camp etiqueta={t('sistema.versions.fins')}>
              <input
                className="input"
                type="datetime-local"
                value={isoALocal(m.fins)}
                aria-invalid={!datesOk}
                onChange={(e) => setM({ ...m, fins: localAIso(e.target.value) })}
              />
            </Camp>
          </div>
          {!datesOk ? <p className="error-text">{t('sistema.versions.datesInvalides')}</p> : null}
          <p className="camp__ajuda" style={{ margin: '-6px 0 0' }}>
            {t('sistema.versions.ajudaVigencia')}
          </p>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <Camp etiqueta={t('versions.versioMin')}>
              <input
                className="input mono"
                value={m.versioMin ?? ''}
                placeholder="2.0"
                aria-invalid={!versioValida(m.versioMin)}
                onChange={(e) => setM({ ...m, versioMin: e.target.value.trim() || null })}
              />
            </Camp>
            <Camp etiqueta={t('versions.versioMax')}>
              <input
                className="input mono"
                value={m.versioMax ?? ''}
                placeholder="2.9"
                aria-invalid={!versioValida(m.versioMax)}
                onChange={(e) => setM({ ...m, versioMax: e.target.value.trim() || null })}
              />
            </Camp>
          </div>
          {!versionsOk ? (
            <p className="error-text">{t('sistema.versions.formatVersio')}</p>
          ) : !rangOk ? (
            <p className="error-text">{t('sistema.versions.rangInvalid')}</p>
          ) : null}
          <label className="switch-row">
            {t('comu.actiu')}
            <input
              type="checkbox"
              className="switch"
              checked={m.actiu}
              onChange={(e) => setM({ ...m, actiu: e.target.checked })}
            />
          </label>
          <ErrorDesa error={desa.error} />
        </div>
        <div className="stack">
          <div className="segmented" role="group" aria-label={t('versions.previa')}>
            {IDIOMES_PREVIA.map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={idiomaPrevia === l}
                onClick={() => setIdiomaPrevia(l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <PreviaMobil
            titol={idiomaPrevia === 'es' ? m.titolEs : m.titolCa}
            cos={idiomaPrevia === 'es' ? m.cosEs : m.cosCa}
          />
        </div>
      </div>
    </Modal>
  );
}

export function W30Versions() {
  const { t, i18n } = useTranslation('administracio');
  const { pot } = useMe();
  const f = useFormat();
  const versions = useAppVersions();
  const missatges = useMissatgesAdmin();
  const elimina = useElimina('/app/missatges', ['app', 'missatges']);
  const missatgeError = useMissatgeError();
  const [pestanya, setPestanya] = useState<Pestanya>('versions');
  const [novaVersio, setNovaVersio] = useState(false);
  const [missatge, setMissatge] = useState<MissatgeAdminDto | null>(null);
  const [eliminant, setEliminant] = useState<MissatgeAdminDto | null>(null);
  const [seleccionat, setSeleccionat] = useState<number | null>(null);
  const es = i18n.language === 'es';
  const llista = useMemo(
    () =>
      [...(versions.data ?? [])].sort(
        (a, b) => a.plataforma.localeCompare(b.plataforma) || comparaVersions(b.versio, a.versio),
      ),
    [versions.data],
  );
  const minima = (p: Plataforma) =>
    versioMaxima(
      llista.filter((v) => v.plataforma === p && v.minimaObligatoria).map((v) => v.versio),
    );
  const darrera = (p: Plataforma) =>
    versioMaxima(llista.filter((v) => v.plataforma === p).map((v) => v.versio));
  const llistaMissatges = missatges.data ?? [];
  const mostrat =
    llistaMissatges.find((m) => m.id === seleccionat) ??
    llistaMissatges.find((m) => m.actiu) ??
    llistaMissatges[0];
  const vigencia = (m: MissatgeAdminDto) =>
    m.desde || m.fins
      ? `${m.desde ? f.dataHora(m.desde) : '…'} – ${m.fins ? f.dataHora(m.fins) : '…'}`
      : t('versions.indefinit');
  const potAlta = pot('cai.app-versions', 'ALTA');
  const nouMissatge = () =>
    setMissatge({
      tipus: 'INFO',
      titolCa: '',
      titolEs: '',
      cosCa: '',
      cosEs: '',
      plataformes: ['IOS', 'ANDROID'],
      actiu: true,
    });

  return (
    <section className="pantalla" data-screen="W-30">
      <Toolbar titol={t('titols.versions')}>
        {potAlta && pestanya === 'versions' ? (
          <button
            type="button"
            className="btn btn--dark btn--s"
            onClick={() => setNovaVersio(true)}
          >
            <Icona nom="arrow-up" />
            {t('versions.nova')}
          </button>
        ) : null}
        {potAlta && pestanya === 'missatges' ? (
          <button type="button" className="btn btn--dark btn--s" onClick={nouMissatge}>
            <Icona nom="plus" />
            {t('versions.nouMissatge')}
          </button>
        ) : null}
      </Toolbar>
      <div className="fitxa-pestanyes" role="tablist" aria-label={t('titols.versions')}>
        <button
          type="button"
          role="tab"
          aria-selected={pestanya === 'versions'}
          onClick={() => setPestanya('versions')}
        >
          {t('sistema.versions.pestanyaVersions')}
          <span className="fitxa-pestanyes__n">{llista.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pestanya === 'missatges'}
          onClick={() => setPestanya('missatges')}
        >
          {t('versions.missatges')}
          <span className="fitxa-pestanyes__n">{llistaMissatges.length}</span>
        </button>
      </div>

      {pestanya === 'versions' ? (
        <div className="content stack">
          <div className="sis-resum-versions">
            {PLATAFORMES.map((p) => (
              <div key={p} className="panel sis-resum-versions__targeta">
                <div className="hstack gap-6 strong">
                  <Icona nom={p === 'IOS' ? 'apple' : 'android'} />
                  {t(`versions.plataformes.${p}`)}
                </div>
                <dl className="sis-dl sis-dl--fila">
                  <div>
                    <dt>{t('sistema.versions.darrera')}</dt>
                    <dd className="mono">{darrera(p) ?? t('versions.cap')}</dd>
                  </div>
                  <div>
                    <dt>{t('sistema.versions.minima')}</dt>
                    <dd className="mono">{minima(p) ?? t('versions.cap')}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
          <div className="panel">
            <CapPanell titol={t('versions.historial')} />
            {versions.isError ? (
              <ErrorCarrega onReintenta={() => void versions.refetch()} />
            ) : llista.length === 0 ? (
              <Buit icona="archive">
                {versions.isLoading ? t('comu.carregant') : t('versions.senseVersions')}
              </Buit>
            ) : (
              <div className="sis-taula-scroll">
                <table className="taula">
                  <thead>
                    <tr>
                      <th>{t('versions.plataforma')}</th>
                      <th>{t('versions.versio')}</th>
                      <th>{t('versions.build')}</th>
                      <th>{t('versions.publicada')}</th>
                      <th>{t('versions.notes')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {llista.map((v) => (
                      <tr key={v.id ?? `${v.plataforma}-${v.versio}`}>
                        <td>
                          <span className="hstack gap-6">
                            <Icona nom={v.plataforma === 'IOS' ? 'apple' : 'android'} />
                            {t(`versions.plataformes.${v.plataforma}`)}
                          </span>
                        </td>
                        <td className="mono">{v.versio}</td>
                        <td className="dim">{v.build}</td>
                        <td>{v.publicadaAt ? f.data(v.publicadaAt) : ''}</td>
                        <td className="dim">{es ? v.notesEs : v.notesCa}</td>
                        <td>
                          {v.minimaObligatoria ? (
                            <span className="badge badge--critic">
                              {t('versions.minimaObligatoria')}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="content admin-mes-lateral">
          <div className="panel">
            {missatges.isError ? (
              <ErrorCarrega onReintenta={() => void missatges.refetch()} />
            ) : llistaMissatges.length === 0 ? (
              <Buit icona="megaphone">
                {missatges.isLoading ? t('comu.carregant') : t('versions.senseMissatges')}
              </Buit>
            ) : (
              <div className="sis-taula-scroll">
                <table className="taula taula--clicable">
                  <thead>
                    <tr>
                      <th>{t('versions.missatge')}</th>
                      <th>{t('versions.tipus')}</th>
                      <th>{t('versions.vigencia')}</th>
                      <th>{t('versions.estat')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {llistaMissatges.map((m) => (
                      <tr
                        key={m.id}
                        aria-selected={m.id === mostrat?.id}
                        className={m.id === mostrat?.id ? 'sis-fila-sel' : undefined}
                        onClick={() => setSeleccionat(m.id ?? null)}
                      >
                        <td>
                          <button
                            type="button"
                            className="sis-enllac-fila"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSeleccionat(m.id ?? null);
                            }}
                          >
                            {es ? m.titolEs : m.titolCa}
                          </button>
                          <div className="t-caption-2 dim">
                            {m.plataformes.map((p) => t(`versions.plataformes.${p}`)).join(', ')}
                          </div>
                        </td>
                        <td>{t(`versions.tipusMissatge.${m.tipus}`)}</td>
                        <td className="t-footnote">{vigencia(m)}</td>
                        <td>
                          <span className={`badge ${m.actiu ? 'badge--ok' : 'badge--gray'}`}>
                            {m.actiu ? t('comu.actiu') : t('comu.inactiu')}
                          </span>
                        </td>
                        <td className="nowrap" onClick={(e) => e.stopPropagation()}>
                          {pot('cai.app-versions', 'MODIFICAR') ? (
                            <button
                              type="button"
                              className="btn btn--ghost btn--xs"
                              aria-label={t('versions.editaMissatge')}
                              title={t('versions.editaMissatge')}
                              onClick={() => setMissatge(m)}
                            >
                              <Icona nom="pencil" />
                            </button>
                          ) : null}
                          {pot('cai.app-versions', 'ELIMINAR') && m.id != null ? (
                            <button
                              type="button"
                              className="btn btn--ghost btn--xs"
                              aria-label={t('comu.elimina')}
                              title={t('comu.elimina')}
                              onClick={() => setEliminant(m)}
                            >
                              <Icona nom="trash" className="c-red" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="panel">
            <CapPanell titol={t('versions.previsualitzacio')} />
            {mostrat ? (
              <PreviaMobil
                titol={es ? mostrat.titolEs : mostrat.titolCa}
                cos={es ? mostrat.cosEs : mostrat.cosCa}
              />
            ) : (
              <Buit icona="phone">{t('sistema.versions.triaMissatge')}</Buit>
            )}
          </div>
        </div>
      )}
      {novaVersio ? <NovaVersio onTanca={() => setNovaVersio(false)} /> : null}
      {missatge ? <EditaMissatge inicial={missatge} onTanca={() => setMissatge(null)} /> : null}
      {eliminant ? (
        <Confirmacio
          titol={t('sistema.versions.eliminaTitol')}
          textAccio={t('comu.elimina')}
          perill
          ocupat={elimina.isPending}
          onTanca={() => setEliminant(null)}
          onConfirma={() =>
            elimina.mutate(eliminant.id as number, { onSuccess: () => setEliminant(null) })
          }
        >
          <p>
            {t('sistema.versions.eliminaText', {
              titol: es ? eliminant.titolEs : eliminant.titolCa,
            })}
          </p>
          {elimina.isError ? <p className="error-text">{missatgeError(elimina.error)}</p> : null}
        </Confirmacio>
      ) : null}
    </section>
  );
}
